import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { PlayCircle, Bell, BellOff, Loader2 } from 'lucide-react';
import { formatNyDateTime } from '../../utils/datetime';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${sessionStorage.getItem('access_token')}` });

/**
 * Card no Admin/Sistema pra gerenciar a verificação automática diária de duplicatas.
 * - Toggle enabled/disabled (cron 3am)
 * - Mostra última execução (quando, por quem, quantas encontrou)
 * - Botão "Rodar agora"
 */
const DupCheckSettingsCard = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [cfg, setCfg] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/settings/dup-check`, { headers: authHeaders() });
      setCfg(data);
    } catch (e) {
      toast.error('Falha ao carregar config');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async () => {
    if (!cfg) return;
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings/dup-check`, { enabled: !cfg.enabled }, { headers: authHeaders() });
      toast.success(!cfg.enabled ? '✅ Verificação diária ativada' : '⏸️ Verificação diária desativada');
      await load();
    } catch (e) {
      toast.error('Falha ao atualizar');
    } finally {
      setSaving(false);
    }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await axios.post(`${API}/admin/settings/dup-check/run-now`, {}, { headers: authHeaders() });
      if (data.groups > 0) {
        toast.warning(`⚠️ ${data.groups} grupo(s) de duplicatas encontrados (${data.duplicates} bill(s) extras)${data.notified ? ' — notificação enviada' : ''}`);
      } else {
        toast.success('✅ Nenhuma duplicata detectada');
      }
      await load();
    } catch (e) {
      toast.error('Falha ao executar verificação');
    } finally {
      setRunning(false);
    }
  };

  const fmt = (iso) => {
    if (!iso) return '—';
    try { return formatNyDateTime(iso); } catch (_) { return iso; }
  };

  const enabled = cfg?.enabled;

  return (
    <div
      data-testid="dup-check-settings-card"
      className={`border rounded-lg p-4 mb-4 ${enabled ? 'bg-blue-50 border-blue-300' : 'bg-gray-50 border-gray-300'}`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-start gap-2">
          {enabled ? (
            <Bell className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
          ) : (
            <BellOff className="h-5 w-5 text-gray-500 flex-shrink-0 mt-0.5" />
          )}
          <div>
            <h4 className={`font-semibold ${enabled ? 'text-blue-800' : 'text-gray-700'}`}>
              🔔 Monitoramento Automático de Duplicatas
            </h4>
            <p className="text-sm text-gray-700 mt-1">
              Roda diariamente às <strong>3:00 (fuso NY)</strong>. Se encontrar duplicatas de Labor Bills, envia notificação in-app pro ADMIN.
              <span className="text-xs text-gray-500 block mt-0.5">Anti-spam: no máximo 1 notificação a cada 20h.</span>
            </p>
          </div>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-gray-500 italic flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Carregando...</p>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3 text-sm bg-white/60 rounded p-3">
            <div>
              <span className="text-gray-500 block text-xs">Última verificação</span>
              <span className="font-medium text-gray-800" data-testid="dup-check-last-at">{fmt(cfg?.last_check_at)}</span>
              {cfg?.last_check_by && <span className="text-xs text-gray-500 block">por {cfg.last_check_by}</span>}
            </div>
            <div>
              <span className="text-gray-500 block text-xs">Resultado</span>
              <span className={`font-semibold ${cfg?.last_check_groups > 0 ? 'text-red-700' : 'text-green-700'}`} data-testid="dup-check-last-result">
                {cfg?.last_check_groups == null
                  ? '—'
                  : cfg.last_check_groups === 0
                    ? 'Nenhuma duplicata 🎉'
                    : `${cfg.last_check_groups} grupo(s), ${cfg.last_check_duplicates} extra(s)`}
              </span>
            </div>
            {cfg?.last_notification_at && (
              <div className="sm:col-span-2">
                <span className="text-gray-500 block text-xs">Última notificação enviada</span>
                <span className="text-gray-700">{fmt(cfg.last_notification_at)}</span>
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2 justify-between">
            {/* Toggle enabled */}
            <label className="inline-flex items-center gap-2 cursor-pointer select-none" data-testid="dup-check-toggle-label">
              <div className="relative">
                <input
                  type="checkbox"
                  checked={!!enabled}
                  onChange={toggle}
                  disabled={saving}
                  className="sr-only peer"
                  data-testid="dup-check-toggle"
                />
                <div className={`w-11 h-6 rounded-full transition-colors ${enabled ? 'bg-blue-600' : 'bg-gray-300'} peer-disabled:opacity-50`}></div>
                <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${enabled ? 'translate-x-5' : ''}`}></div>
              </div>
              <span className={`text-sm font-medium ${enabled ? 'text-blue-800' : 'text-gray-600'}`}>
                {enabled ? 'Verificação diária ativada' : 'Verificação diária desativada'}
              </span>
            </label>

            <Button
              onClick={runNow}
              disabled={running}
              size="sm"
              variant="outline"
              className="border-blue-400 text-blue-700 hover:bg-blue-100"
              data-testid="dup-check-run-now"
            >
              {running ? (
                <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Verificando...</>
              ) : (
                <><PlayCircle className="h-4 w-4 mr-1.5" /> Rodar agora</>
              )}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default DupCheckSettingsCard;
